import { gql } from "@apollo/client";
import { PageDataFragment } from "./PageData";
import { ACFMediaFragment } from "./ACF/Media";
import { FeaturedInducteesFragment } from "./FeaturedInductees";

// Page-level theme settings (font theme, case, accents, nav color)
export const DigitalExhibitSettingsFragment = gql`
  fragment DigitalExhibitSettings on DigitalExhibit {
    digitalExhibitSettings {
      fontTheme
      textCase
      accent1
      accent2
      accent3
      navFontColor
      soundPopupColor
      soundPopupOverride
    }
  }
`;

// Hero — own attached field group, like detailHero on exhibits
export const DigitalExhibitHeroFragment = gql`
  ${ACFMediaFragment}
  fragment DigitalExhibitHero on DigitalExhibit {
    digitalExhibitHero {
      backgroundDesktop {
        backgroundType
        accent
        image {
          ...ACFMedia
        }
      }
      backgroundMobile {
        backgroundType
        accent
        image {
          ...ACFMedia
        }
      }
      videoUrl
      videoUrlMobile
      videoPoster {
        ...ACFMedia
      }
      logo {
        ...ACFMedia
      }
      logoAltText
      subheading
      pillAccent
      arrowOverride {
        ...ACFMedia
      }
      arrowAccent
    }
  }
`;

// Exhibit modules (flexible content, max one instance per layout)
export const DigitalExhibitCardGalleryFragment = gql`
  fragment DigitalExhibitCardGallery on DigitalExhibitContentModulesCardGalleryLayout {
    heading
    headingAccent
    cardAccent
    cardFrontStyle
    knockoutEnabled
    knockoutColor
    shareEnabled
    ogTitle
    ogDescription
    ogImage {
      ...ACFMedia
    }
    cardImageAttributions
    cards {
      frontImage {
        ...ACFMedia
      }
      frontText
      cardBackType
      audioUrl
      quote
      attribution
      backImage {
        ...ACFMedia
      }
      backVideoPreviewUrl
      backYoutubeUrl
    }
  }
`;

export const DigitalExhibitCarouselFragment = gql`
  fragment DigitalExhibitCarousel on DigitalExhibitContentModulesCarouselHighlightLayout {
    heading
    accent
    shareEnabled
    ogTitle
    ogDescription
    ogImage {
      ...ACFMedia
    }
    items {
      mediaType
      image {
        ...ACFMedia
      }
      imageAttribution
      videoLoopUrl
      youtubeUrl
      itemTitle
      songTitle
      caption
    }
  }
`;

export const DigitalExhibitBigTextFragment = gql`
  fragment DigitalExhibitBigText on DigitalExhibitContentModulesBigTextLayout {
    text
    attribution
    accent
    knockoutEnabled
    knockoutColor
    background {
      backgroundType
      accent
      image {
        ...ACFMedia
      }
      imageAttribution
    }
  }
`;

// Deep Dive — relationship to deep-dive CPT;
export const DigitalExhibitDeepDiveFragment = gql`
  ${ACFMediaFragment}
  fragment DigitalExhibitDeepDive on DigitalExhibitContentModulesDeepDiveLayout {
    accent
    shareEnabled
    ogTitle
    ogDescription
    ogImage {
      ...ACFMedia
    }
    deepDive {
      nodes {
        ... on DeepDive {
          slug
          deepDiveFields {
            moduleEyebrow
            moduleHeadline
            moduleBody
            moduleCtaLabel
            moduleImages {
              image { ...ACFMedia }
            }
            imageAttributions
            drawerEyebrow
            drawerHeadline
            body
            drawerContent {
              __typename
              ... on DeepDiveFieldsDrawerContentVideoLayout {
                videoType
                youtubeVideoId
                videoUrl
                videoThumbnail { ...ACFMedia }
                youtubeThumbnail { ...ACFMedia }
              }
              ... on DeepDiveFieldsDrawerContentOutroLayout {
                headline
                outroBody
                images {
                  image { ...ACFMedia }
                  imageAttribution
                  caption
                }
              }
              ... on DeepDiveFieldsDrawerContentQuoteLayout {
                quote
                name
                role
                knockoutEnabled
                knockoutColor
                dimImagery
                imageAttribution
                image { ...ACFMedia }
              }
              ... on DeepDiveFieldsDrawerContentGalleryLayout {
                headline
                galleryItems {
                  image { ...ACFMedia }
                  imageAttribution
                  headline
                  body
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const DigitalExhibitMusicExplorerFragment = gql`
  fragment DigitalExhibitMusicExplorer on DigitalExhibitContentModulesMusicExplorerLayout {
    eyebrow
    headline
    accent
    shareEnabled
    ogTitle
    ogDescription
    ogImage {
      ...ACFMedia
    }
    playlistButton {
      label
      url
    }
    musicExplorerAlbumItems {
      cardType
      albumImage {
        ...ACFMedia
      }
      audioTrack
      songTitle
      songArtist
      songDescription
      playlistImage {
        ...ACFMedia
      }
      playlistHeadline
      playlistBody
      playlistCta {
        label
        url
      }
    }
  }
`;

export const QuizFragment = gql`
  ${ACFMediaFragment}
  fragment QuizFragment on Quiz {
    slug
    quizFields {
      eyebrow
      title
      supportingCopy
      ctaLabel
      image { ...ACFMedia }
      imageAttribution
      knockoutEnabled
      knockoutColor
      dimImagery
      imageMode
      quizType
      resultsImageMode
      resultsImage { ...ACFMedia }
      resultsImageAttribution
      enableShare
      questions {
        questionText
        image { ...ACFMedia }
        imageAttribution
        options { label isCorrect resultKey }
      }
      resultLevels { minScore eyebrow headline message shareText }
      results {
        resultKey
        eyebrow
        headline
        message
        shareText
        image { ...ACFMedia }
        imageAttribution
      }
    }
  }
`;

export const DigitalExhibitQuizFragment = gql`
  ${QuizFragment}
  fragment DigitalExhibitQuiz on DigitalExhibitContentModulesQuizLayout {
    accent
    quiz {
      nodes {
        ...QuizFragment
      }
    }
  }
`;

// Intro + modules + page ender
export const DigitalExhibitContentFragment = gql`
  ${ACFMediaFragment}
  ${FeaturedInducteesFragment}
  ${DigitalExhibitCardGalleryFragment}
  ${DigitalExhibitCarouselFragment}
  ${DigitalExhibitBigTextFragment}
  ${DigitalExhibitDeepDiveFragment}
  ${DigitalExhibitMusicExplorerFragment}
  ${DigitalExhibitQuizFragment}
  fragment DigitalExhibitContent on DigitalExhibit {
    digitalExhibitContent {
      intro {
        statement
        statementAccent
        knockoutEnabled
        knockoutColor
      }
      modules {
        __typename
        ...DigitalExhibitCardGallery
        ...DigitalExhibitCarousel
        ...DigitalExhibitBigText
        ...DigitalExhibitDeepDive
        ...DigitalExhibitMusicExplorer
        ...DigitalExhibitQuiz
      }
      pageEnder {
        tickerAccent
        showCta
        cta {
          type
          eyebrow
          headline
          subhead
          link {
            linkType
            externalLink
            sectionId
            page {
              node {
                uri
              }
            }
          }
        }
        showInductees
        inductees {
          colorOverride
          headline
          cta {
            linkType
            label
            externalLink
            page {
              node {
                uri
              }
            }
          }
          featuredInductees {
            ...FeaturedInductees
          }
        }
        showShop
        shop {
          headline
          type
          shopifyCollection
          shopifyTag
          searchValue
        }
      }
    }
  }
`;

export const DigitalExhibitDetailFragment = gql`
  ${PageDataFragment}
  ${DigitalExhibitSettingsFragment}
  ${DigitalExhibitHeroFragment}
  ${DigitalExhibitContentFragment}
  fragment DigitalExhibitDetailFragment on DigitalExhibit {
    title
    slug
    ...PageData
    ...DigitalExhibitSettings
    ...DigitalExhibitHero
    ...DigitalExhibitContent
  }
`;
